import { useCallback, useEffect, useRef, useState } from 'react';
import type { RuleEntry, RuleSearchHit } from '@gm-binder/shared';
import { api, ApiError } from '../../api/client';
import { Button, EmptyState, Input, MarkdownView, Spinner } from '../ui';
import { Modal } from '../ui/Modal';
import { isSpeechSupported, startListening, type SpeechSession } from '../../lib/speech';
import styles from './RulesAssistant.module.css';

type Phase = 'idle' | 'listening' | 'searching' | 'results' | 'error';

const LISTEN_MS = 45_000;

/**
 * Floating, Siri-style rules assistant. A candle launcher sits bottom-right of a running session;
 * tapping it (or pressing "R") opens a modal that listens to the table for up to 45s, transcribes the
 * speech, matches it against the cached SRD rules and shows the rule — with a typed-query fallback.
 */
export function RulesAssistant() {
  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState<Phase>('idle');
  const [transcript, setTranscript] = useState('');
  const [manual, setManual] = useState('');
  const [results, setResults] = useState<RuleSearchHit[]>([]);
  const [selected, setSelected] = useState<RuleEntry | null>(null);
  const [loadingEntry, setLoadingEntry] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const sessionRef = useRef<SpeechSession | null>(null);
  const speechOk = isSpeechSupported();

  const stopSession = useCallback(() => {
    sessionRef.current?.abort();
    sessionRef.current = null;
  }, []);

  const loadEntry = useCallback(async (hit: RuleSearchHit) => {
    setLoadingEntry(true);
    setSelected(null);
    try {
      const entry = await api.getRuleEntry(hit.type, hit.index);
      setSelected(entry);
    } catch {
      setSelected({
        type: hit.type,
        index: hit.index,
        name: hit.name,
        nameIt: hit.nameIt,
        markdown: '_Dettaglio non disponibile._',
      });
    } finally {
      setLoadingEntry(false);
    }
  }, []);

  const runSearch = useCallback(
    async (raw: string) => {
      const query = raw.trim();
      if (!query) {
        setPhase('idle');
        return;
      }
      setTranscript(query);
      setSelected(null);
      setErrorMsg('');
      setPhase('searching');
      try {
        const res = await api.lookupRules(query, 6);
        setResults(res.results);
        setPhase('results');
        if (res.results[0]) void loadEntry(res.results[0]);
      } catch (e) {
        setErrorMsg(e instanceof ApiError ? e.message : 'Ricerca non riuscita.');
        setPhase('error');
      }
    },
    [loadEntry],
  );

  const listen = useCallback(() => {
    if (!speechOk) {
      setPhase('idle');
      return;
    }
    stopSession();
    setTranscript('');
    setResults([]);
    setSelected(null);
    setErrorMsg('');
    setPhase('listening');
    sessionRef.current = startListening(
      {
        onPartial: setTranscript,
        onFinal: (text) => {
          sessionRef.current = null;
          if (text) void runSearch(text);
          else setPhase('idle');
        },
        onError: (err) => {
          sessionRef.current = null;
          setErrorMsg(
            err === 'not-allowed' || err === 'service-not-allowed'
              ? 'Microfono non autorizzato. Consenti l’accesso o scrivi la regola qui sotto.'
              : 'Non sono riuscito ad ascoltare. Scrivi la regola qui sotto.',
          );
          setPhase('error');
        },
      },
      { lang: 'it-IT', maxMs: LISTEN_MS },
    );
  }, [speechOk, runSearch, stopSession]);

  const openAssistant = useCallback(() => {
    setOpen(true);
    if (speechOk) listen();
    else setPhase('idle');
  }, [speechOk, listen]);

  const close = useCallback(() => {
    stopSession();
    setOpen(false);
    setPhase('idle');
    setTranscript('');
    setManual('');
    setResults([]);
    setSelected(null);
    setErrorMsg('');
  }, [stopSession]);

  useEffect(() => () => stopSession(), [stopSession]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.ctrlKey || e.metaKey || e.altKey) return;
      const target = e.target as HTMLElement | null;
      const typing =
        !!target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.isContentEditable);
      if (typing) return;
      if (e.key.toLowerCase() === 'r') {
        e.preventDefault();
        openAssistant();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [openAssistant]);

  const submitManual = (e: React.FormEvent) => {
    e.preventDefault();
    if (manual.trim()) void runSearch(manual);
  };

  return (
    <>
      <button
        type="button"
        className={styles.launcher}
        onClick={openAssistant}
        aria-label="Cerca nelle regole (assistente vocale) — premi R"
        title="Cerca nelle regole — premi R"
      >
        <span className={styles.launcherIcon} aria-hidden>
          🕯️
        </span>
      </button>

      <Modal open={open} onClose={close} title="🕯️ Cerca nelle regole" width={640}>
        <div className={styles.body}>
          {phase === 'listening' && (
            <div className={styles.listening}>
              <div className={styles.pulse} aria-hidden>
                <span className={styles.mic}>🎙️</span>
              </div>
              <p className={styles.listenLabel}>Sto ascoltando il tavolo…</p>
              <p className={styles.transcript}>
                {transcript || 'Parla della regola che ti serve (fino a 45 secondi).'}
              </p>
              <div className={styles.actions}>
                <Button variant="primary" onClick={() => sessionRef.current?.stop()}>
                  Cerca ora
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => {
                    stopSession();
                    setPhase('idle');
                  }}
                >
                  Ferma
                </Button>
              </div>
            </div>
          )}

          {phase === 'searching' && (
            <div className={styles.center}>
              <Spinner />
              <span>Cerco la regola…</span>
            </div>
          )}

          {(phase === 'results' || phase === 'error' || phase === 'idle') && (
            <div className={styles.results}>
              {transcript && (
                <p className={styles.heard}>
                  <span className={styles.heardLabel}>Ho sentito:</span> {transcript}
                </p>
              )}

              {phase === 'error' && (
                <EmptyState icon="⚠️" title="Qualcosa è andato storto" description={errorMsg} />
              )}

              {phase === 'results' && results.length === 0 && (
                <EmptyState
                  icon="📖"
                  title="Nessuna regola trovata"
                  description="Prova a riformulare o scrivi la regola qui sotto."
                />
              )}

              {phase === 'results' && results.length > 0 && (
                <>
                  <div className={styles.entry}>
                    {loadingEntry || !selected ? (
                      <div className={styles.center}>
                        <Spinner />
                      </div>
                    ) : (
                      <>
                        <h3 className={styles.entryTitle}>
                          {selected.nameIt ? `${selected.nameIt} ` : ''}
                          <span className={styles.entryEn}>{selected.name}</span>
                        </h3>
                        <MarkdownView>{selected.markdown}</MarkdownView>
                      </>
                    )}
                  </div>

                  {results.length > 1 && (
                    <div className={styles.alternates}>
                      <span className={styles.alternatesLabel}>Altri risultati</span>
                      <div className={styles.chips}>
                        {results.map((hit) => (
                          <button
                            key={`${hit.type}/${hit.index}`}
                            type="button"
                            className={[
                              styles.chip,
                              selected?.index === hit.index && selected?.type === hit.type
                                ? styles.chipActive
                                : undefined,
                            ]
                              .filter(Boolean)
                              .join(' ')}
                            onClick={() => void loadEntry(hit)}
                          >
                            {hit.nameIt ?? hit.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}

              <form className={styles.manual} onSubmit={submitManual}>
                {speechOk && (
                  <Button type="button" variant="ghost" onClick={listen} aria-label="Ascolta di nuovo">
                    🎙️
                  </Button>
                )}
                <Input
                  value={manual}
                  onChange={(e) => setManual(e.target.value)}
                  placeholder="Oppure scrivi una regola o una condizione…"
                  aria-label="Cerca una regola"
                />
                <Button type="submit" variant="primary">
                  Cerca
                </Button>
              </form>

              <p className={styles.note}>
                Le regole provengono dall’SRD 5.1 (in inglese) e sono salvate in cache per l’uso
                offline.
              </p>
            </div>
          )}
        </div>
      </Modal>
    </>
  );
}
